import React, { useState, useEffect } from 'react';
import autoResponderService, { AutoResponder, CreateAutoResponderData } from '../../../services/autoResponderService';
import {
  BoltIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';

const AutoRespondersPage: React.FC = () => {
  const [responders, setResponders] = useState<AutoResponder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingResponder, setEditingResponder] = useState<AutoResponder | null>(null);
  
  const [formData, setFormData] = useState<CreateAutoResponderData>({
    name: '',
    triggerType: 'NEW_PATRON',
    delayMinutes: 0,
    subject: '',
    message: '',
    isActive: true,
  });

  useEffect(() => {
    loadResponders();
  }, []);

  const loadResponders = async () => {
    try {
      setLoading(true);
      const data = await autoResponderService.getAll();
      setResponders(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (error) {
      console.error('Failed to load auto responders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingResponder) {
        await autoResponderService.update(editingResponder.id, formData);
      } else {
        await autoResponderService.create(formData);
      }
      setShowModal(false);
      resetForm();
      loadResponders();
    } catch (error) {
      console.error('Failed to save auto responder:', error);
      alert('Failed to save auto responder. Please try again.');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await autoResponderService.toggle(id);
      loadResponders();
    } catch (error) {
      console.error('Failed to toggle auto responder:', error);
    }
  };

  const handleTest = async (id: string) => {
    try {
      await autoResponderService.test(id);
      alert('Test message sent successfully! Check your messages.');
    } catch (error) {
      console.error('Failed to test auto responder:', error);
      alert('Failed to send test message.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this auto responder?')) return;
    try {
      await autoResponderService.delete(id);
      loadResponders();
    } catch (error) {
      console.error('Failed to delete auto responder:', error);
      alert('Failed to delete auto responder.');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingResponder(null);
    setShowModal(true);
  };

  const openEditModal = (responder: AutoResponder) => {
    setEditingResponder(responder);
    setFormData({
      name: responder.name,
      triggerType: responder.triggerType,
      delayMinutes: responder.delayMinutes,
      subject: responder.subject,
      message: responder.message,
      tierIds: responder.tierIds,
      isActive: responder.isActive,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      triggerType: 'NEW_PATRON',
      delayMinutes: 0,
      subject: '',
      message: '',
      isActive: true,
    });
  };

  const getTriggerBadge = (type: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      NEW_PATRON: { bg: 'bg-green-100', text: 'text-green-800', label: 'New Patron' },
      TIER_CHANGE: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Tier Change' },
      ANNIVERSARY: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Anniversary' },
      CUSTOM_DATE: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Custom Date' },
    };
    const badge = badges[type] || badges.NEW_PATRON;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const formatDelay = (minutes: number) => {
    if (minutes === 0) return 'Immediate';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BoltIcon className="h-10 w-10 text-yellow-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Auto Responders</h1>
                <p className="text-gray-600 mt-1">Automated messages triggered by patron actions</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadResponders}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Auto Responder
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total Responders</p>
            <p className="text-2xl font-bold text-gray-900">{responders.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Active</p>
            <p className="text-2xl font-bold text-green-600">
              {responders.filter(r => r.isActive).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total Sent</p>
            <p className="text-2xl font-bold text-blue-600">
              {responders.reduce((sum, r) => sum + (r.sentCount || 0), 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Inactive</p>
            <p className="text-2xl font-bold text-gray-600">
              {responders.filter(r => !r.isActive).length}
            </p>
          </div>
        </div>

        {/* Responders List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 bg-white rounded-lg shadow">
              <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-600">Loading auto responders...</span>
            </div>
          ) : responders.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <BoltIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No auto responders</h3>
              <p className="mt-1 text-sm text-gray-500">Create your first automated message</p>
              <div className="mt-6">
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Auto Responder
                </button>
              </div>
            </div>
          ) : (
            responders.map((responder) => (
              <div
                key={responder.id}
                className={`bg-white rounded-lg shadow p-6 ${!responder.isActive ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{responder.name}</h3>
                      {getTriggerBadge(responder.triggerType)}
                      <span className="text-xs text-gray-500">
                        Delay: {formatDelay(responder.delayMinutes)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-2">{responder.message}</p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                      <span>Sent: {responder.sentCount || 0} times</span>
                      {responder.tierIds && responder.tierIds.length > 0 && <span>Target Tiers: {responder.tierIds.length}</span>}
                      <span>Created: {new Date(responder.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <div className="ml-4 flex items-center gap-2">
                    <button
                      onClick={() => handleToggle(responder.id)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        responder.isActive ? 'bg-yellow-600' : 'bg-gray-200'
                      }`}
                      title={responder.isActive ? 'Disable' : 'Enable'}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          responder.isActive ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => handleTest(responder.id)}
                      className="text-green-600 hover:text-green-900"
                      title="Send test message"
                    >
                      <BeakerIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => openEditModal(responder)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(responder.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
              
              <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
                <form onSubmit={handleSubmit}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {editingResponder ? 'Edit Auto Responder' : 'Create Auto Responder'}
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                          placeholder="Welcome Message"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Type *</label>
                        <select
                          value={formData.triggerType}
                          onChange={(e) => setFormData(prev => ({ ...prev, triggerType: e.target.value as any }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="NEW_PATRON">New Patron</option>
                          <option value="TIER_CHANGE">Tier Change</option>
                          <option value="ANNIVERSARY">Anniversary</option>
                          <option value="CUSTOM_DATE">Custom Date</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Delay (minutes) *</label>
                        <input
                          type="number"
                          required
                          min="0"
                          value={formData.delayMinutes}
                          onChange={(e) => setFormData(prev => ({ ...prev, delayMinutes: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                          placeholder="0"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          0 = immediate, 60 = 1 hour, 1440 = 1 day
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                        <input
                          type="text"
                          required
                          value={formData.subject}
                          onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                          placeholder="Welcome to my page!"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                        <textarea
                          required
                          value={formData.message}
                          onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                          placeholder="Welcome {{patron_name}}! Thank you for joining my {{tier_name}} tier!"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Available variables: {'{{patron_name}}'}, {'{{tier_name}}'}, {'{{amount}}'}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Target Tier IDs (comma-separated, optional)</label>
                        <input
                          type="text"
                          value={formData.tierIds?.join(', ') || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            tierIds: e.target.value.split(',').map(t => t.trim()).filter(Boolean) 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                          placeholder="Leave empty for all tiers"
                        />
                      </div>

                      <div className="flex items-center">
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                            className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 mr-2"
                          />
                          <span>Active immediately</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                    >
                      {editingResponder ? 'Update' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoRespondersPage;
